import { useEffect, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Badge, Card, EmptyState, Spinner, Alert, Button } from '@agion/ui'
import { Siren, ShieldCheck, AlertTriangle, ChevronDown, ChevronRight, Clock } from 'lucide-react'
import { PageHeader } from '@/layouts/InfraPageHeader'
import { listIncidents, listTimelineEvents } from './infra_incident.service'

type IncidentSeverity = 'critical' | 'high' | 'medium' | 'low'
type IncidentStatus = 'open' | 'investigating' | 'resolved'

interface TimelineEvent {
  id: string
  timestamp: string
  description: string
  actor: string
}

interface Incident {
  id: string
  title: string
  severity: IncidentSeverity
  status: IncidentStatus
  startedAt: string
  duration: string
  timeline: TimelineEvent[]
  timelineUnavailable: boolean
}

function severityBadgeVariant(severity: IncidentSeverity) {
  switch (severity) {
    case 'critical':
      return 'error' as const
    case 'high':
      return 'warning' as const
    case 'medium':
      return 'info' as const
    case 'low':
      return 'default' as const
  }
}

function statusBadgeVariant(status: IncidentStatus) {
  switch (status) {
    case 'open':
      return 'error' as const
    case 'investigating':
      return 'warning' as const
    case 'resolved':
      return 'success' as const
  }
}

export function IncidentTimelinePage() {
  const { t } = useTranslation(['pages'])
  const [isLoading, setIsLoading] = useState(true)
  const [unavailable, setUnavailable] = useState(false)
  const [incidents, setIncidents] = useState<Incident[]>([])
  const [expandedId, setExpandedId] = useState<string | null>(null)

  const loadIncidents = useCallback(async () => {
    try {
      setIsLoading(true)
      setUnavailable(false)
      const response = await listIncidents()
      const mapped: Incident[] = await Promise.all(
        response.data.map(async (inc) => {
          const tlResponse = await listTimelineEvents(inc.id).catch(() => null)
          const timeline: TimelineEvent[] = (tlResponse?.data ?? []).map(ev => ({
            id: ev.id,
            timestamp: ev.createdAt,
            description: ev.message,
            actor: ev.author,
          }))
          const severity = (
            inc.severity === 'major' ? 'high'
            : inc.severity === 'minor' ? 'medium'
            : inc.severity === 'info' ? 'low'
            : inc.severity
          ) as IncidentSeverity
          const status = (
            inc.status === 'identified' ? 'investigating'
            : inc.status === 'closed' ? 'resolved'
            : inc.status
          ) as IncidentStatus
          const startMs = new Date(inc.createdAt).getTime()
          const endMs = inc.resolvedAt ? new Date(inc.resolvedAt).getTime() : Date.now()
          const mins = Math.round((endMs - startMs) / 60000)
          const duration = mins >= 60 ? `${Math.floor(mins / 60)}h ${mins % 60}m` : `${mins}m`
          return {
            id: inc.id,
            title: inc.title,
            severity,
            status,
            startedAt: inc.createdAt,
            duration,
            timeline,
            timelineUnavailable: tlResponse === null,
          }
        })
      )
      setIncidents(mapped)
    } catch {
      setUnavailable(true)
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    void loadIncidents()
  }, [loadIncidents])

  function toggleExpanded(id: string) {
    setExpandedId((prev) => (prev === id ? null : id))
  }

  return (
    <div className="p-6 space-y-6">
      <PageHeader
        icon={Siren}
        iconColor="text-rose-600"
        title={t('pages:incidents.title', 'Incidents')}
        description={t('pages:incidents.description', 'Incident history and timeline')}
        actions={<Button variant="ghost" disabled={isLoading} onClick={() => void loadIncidents()}>{t('pages:incidents.retry', 'Retry')}</Button>}
      />

      {unavailable && (
        <Alert variant="info">
          {t('pages:incidents.unavailable', 'Incident data could not be loaded. Retry to check its current status.')}
        </Alert>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Spinner />
        </div>
      ) : unavailable && incidents.length === 0 ? (
        <EmptyState
          icon={AlertTriangle}
          title={t('pages:incidents.awaitingBackendTitle', 'Incident data unavailable')}
          description={t('pages:incidents.awaitingBackend', 'Unable to confirm incident history. Try again.')}
        />
      ) : incidents.length === 0 ? (
        <EmptyState
          icon={ShieldCheck}
          title={t('pages:incidents.empty', 'No incidents recorded')}
          description={t('pages:incidents.emptyDesc', 'No incidents have been reported for your environments.')}
        />
      ) : (
        <Card>
          <div className="grid grid-cols-5 gap-4 px-4 py-3 bg-slate-50 dark:bg-slate-800/50 text-xs font-medium text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700">
            <span>{t('pages:incidents.columns.title', 'Title')}</span>
            <span>{t('pages:incidents.columns.severity', 'Severity')}</span>
            <span>{t('pages:incidents.columns.status', 'Status')}</span>
            <span>{t('pages:incidents.columns.started', 'Started')}</span>
            <span>{t('pages:incidents.columns.duration', 'Duration')}</span>
          </div>
          <div className="divide-y divide-slate-200 dark:divide-slate-700">
            {incidents.map((incident) => (
              <div key={incident.id}>
                {/* Incident row */}
                <div
                  className="grid grid-cols-5 gap-4 px-4 py-3 text-sm items-center cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50"
                  role="button"
                  tabIndex={0}
                  onClick={() => toggleExpanded(incident.id)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleExpanded(incident.id) } }}
                >
                  <span className="flex items-center gap-2 text-slate-900 dark:text-slate-100">
                    {expandedId === incident.id ? (
                      <ChevronDown className="h-4 w-4 text-slate-400 shrink-0" />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-slate-400 shrink-0" />
                    )}
                    {incident.title}
                  </span>
                  <span>
                    <Badge variant={severityBadgeVariant(incident.severity)}>
                      {incident.severity}
                    </Badge>
                  </span>
                  <span>
                    <Badge variant={statusBadgeVariant(incident.status)}>
                      {incident.status}
                    </Badge>
                  </span>
                  <span className="text-slate-500 dark:text-slate-400 text-xs">
                    {incident.startedAt}
                  </span>
                  <span className="text-slate-500 dark:text-slate-400 text-xs">
                    {incident.duration}
                  </span>
                </div>

                {expandedId === incident.id && incident.timelineUnavailable && (
                  <Alert variant="warning">{t('pages:incidents.timelineUnavailable', 'Timeline unavailable for this incident.')}</Alert>
                )}
                {/* Timeline (expanded) */}
                {expandedId === incident.id && incident.timeline.length > 0 && (
                  <div className="px-4 pb-4 pl-12 bg-slate-50/50 dark:bg-slate-800/20">
                    <div className="border-l-2 border-slate-200 dark:border-slate-700 pl-4 space-y-3 py-2">
                      {incident.timeline.map((event) => (
                        <div key={event.id} className="relative">
                          <div className="absolute -left-[21px] top-1.5 w-2 h-2 rounded-full bg-slate-400 dark:bg-slate-500" />
                          <div>
                            <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                              <Clock className="h-3 w-3" />
                              <span>{event.timestamp}</span>
                              <span className="text-slate-400">-</span>
                              <span>{event.actor}</span>
                            </div>
                            <p className="text-sm text-slate-700 dark:text-slate-300 mt-0.5">
                              {event.description}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  )
}
